import { useState, useEffect } from 'react';

interface CountdownResult {
    text: string;
    isLessThan24Hours: boolean;
}

/**
 * Custom hook for countdown timer
 * Shows days when > 24 hours remaining
 * Shows live HH:MM:SS countdown when < 24 hours remaining
 */
export const useCountdown = (endDate: string | undefined): CountdownResult => {
    const [currentTime, setCurrentTime] = useState(Date.now());

    const getTimeRemaining = (): CountdownResult => {
        if (!endDate) return { text: '', isLessThan24Hours: false };

        try {
            const end = new Date(endDate);
            const now = currentTime;
            const diffTime = end.getTime() - now;

            if (diffTime < 0) return { text: 'Ended', isLessThan24Hours: false };

            const diffSeconds = Math.floor(diffTime / 1000);
            const diffMinutes = Math.floor(diffSeconds / 60);
            const diffHours = Math.floor(diffMinutes / 60);
            const diffDays = Math.floor(diffHours / 24);

            // If less than 24 hours, show HH:MM:SS countdown
            if (diffHours < 24) {
                const hours = diffHours;
                const minutes = diffMinutes % 60;
                const seconds = diffSeconds % 60;
                const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                return { text: timeStr, isLessThan24Hours: true };
            }

            // Otherwise show days
            if (diffDays === 1) return { text: '1 day', isLessThan24Hours: false };
            return { text: `${diffDays} days`, isLessThan24Hours: false };
        } catch (error) {
            return { text: '', isLessThan24Hours: false };
        }
    };

    const result = getTimeRemaining();

    // Update timer every second when less than 24 hours
    useEffect(() => {
        if (!endDate) return;

        if (result.isLessThan24Hours) {
            const interval = setInterval(() => {
                setCurrentTime(Date.now());
            }, 1000);

            return () => clearInterval(interval);
        }
    }, [endDate, result.isLessThan24Hours]);

    return result;
};
